import React, { useEffect, useState } from 'react'
import GradientButton from '../../components/GradientButton'

type TranslateFn = (key: any, vars?: Record<string, string | number>) => string

export interface DeleteProductionRowButtonProps {
  /** Whether this row is the one currently armed for confirm (host owns the
   * single `useTwoStepConfirm` so only one row is armed app-wide at a time). */
  armed: boolean
  /** Disable both buttons — e.g. while a delete request is in flight. */
  disabled?: boolean
  /** How many Tier A events the delete will also remove (mortality + weight +
   * fish-stock + harvest). Shown in the confirm label so the operator knows
   * the blast radius before the second tap. */
  linkedEventCount: number
  onArm: () => void
  /** Findings #66: when the row has linked events, their delete is audited
   * and needs a reason; `reason` is '' when there are none. */
  onConfirm: (reason: string) => void
  onCancel: () => void
  t: TranslateFn
  /** Prefix for the `data-testid` on the primary button (`<prefix>` and
   * `<prefix>-cancel`). */
  testId?: string
}

/**
 * Two-step (button-flip) confirm for deleting a `ProductionRecord` row and its
 * linked Tier A events. Shared by the Biomass recent-log lists and the
 * Database table so both surfaces confirm identically.
 *
 * v2.1 finding #83: `window.confirm()` / modal dialogs are silently suppressed
 * by iOS Chrome in some handler contexts, so destructive actions across the
 * app use the in-app flip pattern (`useTwoStepConfirm`) instead. First tap
 * arms (button turns red, label becomes "Confirm delete…"), second tap runs
 * `onConfirm`; a sibling Cancel clears the armed state.
 */
export function DeleteProductionRowButton(props: DeleteProductionRowButtonProps): React.ReactElement {
  const { armed, disabled = false, linkedEventCount, onArm, onConfirm, onCancel, t, testId } = props
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (!armed) setReason('')
  }, [armed])
  const needsReason = armed && linkedEventCount > 0
  const reasonMissing = needsReason && reason.trim() === ''

  const primaryLabel = armed
    ? (linkedEventCount > 0
      ? t('productionRow.delete.confirmWithEvents', { count: linkedEventCount })
      : t('productionRow.delete.confirm'))
    : t('common.delete')

  return (
    <>
      {needsReason ? (
        <input
          type="text"
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('productionRow.delete.reasonPlaceholder')}
          aria-label={t('productionRow.delete.reasonPlaceholder')}
          data-testid={testId ? `${testId}-reason` : undefined}
          style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 180 }}
        />
      ) : null}
      <GradientButton
        type="button"
        size="sm"
        variant="danger"
        data-testid={testId}
        disabled={disabled || reasonMissing}
        title={reasonMissing ? t('productionRow.delete.reasonRequired') : undefined}
        onClick={() => {
          if (!armed) {
            onArm()
            return
          }
          if (reasonMissing) return
          onConfirm(reason.trim())
        }}
      >
        {primaryLabel}
      </GradientButton>
      {armed ? (
        <GradientButton
          type="button"
          size="sm"
          variant="secondary"
          data-testid={testId ? `${testId}-cancel` : undefined}
          disabled={disabled}
          onClick={onCancel}
        >
          {t('common.cancel')}
        </GradientButton>
      ) : null}
    </>
  )
}
